/**
 * React hook fronting the parser Worker, with an inline fallback when
 * Worker construction fails (older engines, restricted contexts).
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { runParse } from '../lib/pipeline';
import type { ParseRequest, ParseResult } from '../lib/types';
import type { ParseErrorMessage, ParseResultMessage, WorkerRequest } from './parser.worker';

interface Pending {
  resolve: (result: ParseResult) => void;
  reject: (error: Error) => void;
}

export function useParser(): {
  parse: (request: ParseRequest) => Promise<ParseResult>;
  usingWorker: boolean;
} {
  const workerRef = useRef<Worker | null>(null);
  const nextIdRef = useRef(1);
  const pendingRef = useRef(new Map<number, Pending>());
  const [usingWorker, setUsingWorker] = useState(true);

  const flushPending = useCallback((error: Error) => {
    const pending = pendingRef.current;
    pendingRef.current = new Map();
    for (const entry of pending.values()) entry.reject(error);
  }, []);

  useEffect(() => {
    let worker: Worker | null = null;
    try {
      worker = new Worker(new URL('./parser.worker.ts', import.meta.url), { type: 'module' });
    } catch {
      setUsingWorker(false);
      return;
    }

    workerRef.current = worker;
    const pending = pendingRef.current;

    const onMessage = (event: MessageEvent<ParseResultMessage | ParseErrorMessage>) => {
      const msg = event.data;
      if (!msg || typeof msg.id !== 'number') return;
      const entry = pending.get(msg.id);
      if (!entry) return;
      pending.delete(msg.id);
      if (msg.ok) entry.resolve(msg.result);
      else entry.reject(new Error(msg.error));
    };
    const onError = () => {
      setUsingWorker(false);
      workerRef.current = null;
      flushPending(new Error('Parser worker crashed; falling back to inline parsing.'));
    };

    worker.addEventListener('message', onMessage);
    worker.addEventListener('error', onError);
    return () => {
      worker.removeEventListener('message', onMessage);
      worker.removeEventListener('error', onError);
      worker.terminate();
      workerRef.current = null;
    };
  }, [flushPending]);

  const parse = useCallback(
    (request: ParseRequest): Promise<ParseResult> => {
      const worker = workerRef.current;
      if (!worker) {
        try {
          return Promise.resolve(runParse(request));
        } catch (err) {
          return Promise.reject(err instanceof Error ? err : new Error(String(err)));
        }
      }
      const id = nextIdRef.current++;
      return new Promise<ParseResult>((resolve, reject) => {
        pendingRef.current.set(id, { resolve, reject });
        const message: WorkerRequest = { id, request };
        try {
          worker.postMessage(message);
        } catch (err) {
          pendingRef.current.delete(id);
          reject(err instanceof Error ? err : new Error(String(err)));
        }
      });
    },
    [],
  );

  return { parse, usingWorker };
}
